import React, { useState } from "react"
import { Button, Tag, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowUp, faTrash } from "@fortawesome/free-solid-svg-icons"
import { describeConditionGroups } from "../../model/conditions"
import { groupWords } from "../../model/nest"
import { rangeName } from "../../model/ranges"
import { RAW_NAMES, ShownNames } from "../../model/shownLabels"
import type { Catalogue, CatalogueQuestion } from "../../types/catalogue"
import { BannerGroup, BannerPoint, isConditionGroup } from "../../types/tabSpecDocument"
import RangeListEditor from "../shared/RangeListEditor.component"
import BannerConditionColumns from "./BannerConditionColumns.component"
import BannerNetModal from "./BannerNetModal.component"
import DraftInput from "./DraftInput.component"
import {
  addBannerNet,
  LAST_COLUMN_STAYS,
  materialisePoints,
  movePoint,
  rangesOf,
  removePoint,
  renamePoint,
  withoutPoint,
  withRanges,
} from "./bannerEdits"

/** A number in a banner: its columns are ranges. */
const NUMBER_TYPES: CatalogueQuestion["type"][] = ["number", "grid-number"]

const RANGE_COLUMNS = "Each range is a column. A range includes both its from and its to value."
const NO_RANGE_COLUMNS = "No ranges yet: the group is left out of the banner until it has one."
/** Above the columns of a number's group that are not ranges. */
const OTHER_COLUMNS = "Other columns of the group, after the ranges:"
/** What an answer or a net in a number's group stands for: nothing. */
const ANSWER_IN_NUMBER = "Left out of the banner: a number's columns are ranges."

export interface BannerGroupEditorProps {
  group: BannerGroup
  /** The group's question; null when the questionnaire no longer has it, and for a group of conditions. */
  question: CatalogueQuestion | null
  /** What the conditions of a group of conditions can name; null while the questionnaire is read. */
  catalogue?: Catalogue | null
  /**
   * The group's heading, unique within the banner (`groupHeadings`): it names
   * the group's controls, so a question in the banner twice can be told apart.
   */
  heading: string
  /** Writes the heading; returns the sentence to show when it is refused, or null. */
  onLabel: (label: string) => string | null
  onPoints: (points: BannerPoint[] | null) => void
  /** Absent for the first group. */
  onMoveUp?: () => void
  /** Absent for the last group. */
  onMoveDown?: () => void
  onRemove: () => void
  /** Where the net dialog renders. */
  getContainer?: () => HTMLElement
  /** The names the tables show (the spec's shown labels); the raw labels by default. */
  names?: ShownNames
}

/**
 * One column group of a banner: its heading and its columns. While the
 * group follows the questionnaire it lists every answer (of its grid row or
 * loop iteration, when it names one); "Choose columns" turns that into a
 * list to reorder, rename, cut down and add nets to. A number's columns are
 * its ranges (`RangeListEditor`), and a group of conditions has columns each
 * with a name and a condition (`BannerConditionColumns`). A group keeps at
 * least one column. Text fields write when they are left. Every answer reads
 * as the tables show it (`names`), its label as the title.
 */
const BannerGroupEditor: React.FC<BannerGroupEditorProps> = ({
  group,
  question,
  catalogue = null,
  heading,
  onLabel,
  onPoints,
  onMoveUp,
  onMoveDown,
  onRemove,
  getContainer,
  names = RAW_NAMES,
}) => {
  const [addingNet, setAddingNet] = useState(false)
  const points = group.points
  const variable = question?.label ?? group.variable
  /** An answer as the tables show it: its shown label, or its label. */
  const optionName = (label: string): string => names.option(variable, label)
  const pointName = (p: BannerPoint): string =>
    p.kind === "category" ? optionName(p.label) : p.name
  const conditions = isConditionGroup(group)
  const number = question !== null && NUMBER_TYPES.includes(question.type)
  // A number's columns that are not ranges, with their places: a condition
  // column, or an answer or a net stored while the question was a choice.
  const otherColumns = number
    ? (points ?? []).flatMap((p, i) => (p.kind === "range" ? [] : [{ p, i }]))
    : []
  // The members are stored by their labels, and shown as the tables show them.
  const netOptions = question
    ? question.options
        .filter((o) => !o.exception)
        .map((o) => ({ value: o.label, label: optionName(o.label), title: o.label }))
    : []
  // The heading the group shows without one of its own: the variable as
  // shown, a variable nested in the data by its parts, its grid row or loop
  // iteration after " - ". A group of conditions has only its own.
  const placeholder = conditions
    ? "Heading"
    : groupWords({ ...group, label: undefined }, catalogue, names)

  const confirmNet = (netName: string, members: string[]): string | null => {
    if (!points) return null
    // Members in questionnaire order, whatever order they were ticked in.
    const next = addBannerNet(
      points,
      netName,
      netOptions.flatMap((o) => (members.includes(o.value) ? [o.value] : []))
    )
    if (typeof next === "string") return next
    onPoints(next)
    setAddingNet(false)
    return null
  }
  const commitRename = (index: number, text: string): string | null => {
    if (!points) return null
    const next = renamePoint(points, index, text, optionName)
    if (typeof next === "string") return next
    onPoints(next)
    return null
  }

  return (
    <section className="ts-banner-group" aria-label={`Group ${heading}`}>
      <div className="ts-banner-group__head">
        <DraftInput
          className="ts-banner-group__heading"
          aria-label={`Heading of ${heading}`}
          placeholder={placeholder}
          value={group.label ?? ""}
          onCommit={onLabel}
        />
        <Button
          size="small"
          type="text"
          aria-label={`Move group ${heading} up`}
          icon={<FontAwesomeIcon icon={faArrowUp} />}
          disabled={!onMoveUp}
          onClick={onMoveUp}
        />
        <Button
          size="small"
          type="text"
          aria-label={`Move group ${heading} down`}
          icon={<FontAwesomeIcon icon={faArrowDown} />}
          disabled={!onMoveDown}
          onClick={onMoveDown}
        />
        <Button
          size="small"
          type="text"
          danger
          aria-label={`Remove group ${heading}`}
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={onRemove}
        />
      </div>

      {conditions ? (
        <BannerConditionColumns
          points={points ?? []}
          onPoints={onPoints}
          catalogue={catalogue}
          heading={heading}
          names={names}
        />
      ) : number ? (
        <div className="ts-banner-group__body">
          <p className="ts-hint">{rangesOf(points).length > 0 ? RANGE_COLUMNS : NO_RANGE_COLUMNS}</p>
          <RangeListEditor
            ranges={rangesOf(points)}
            onChange={(next) => onPoints(withRanges(points, next))}
            owner="column"
            label={`Ranges of ${heading}`}
            others={(points ?? []).flatMap((p) => (p.kind === "range" ? [] : [pointName(p)]))}
          />
          {/* Its other columns: a condition, or an answer kept from when it was a choice question. */}
          {otherColumns.length > 0 && (
            <>
              <p className="ts-hint">{OTHER_COLUMNS}</p>
              <ul className="ts-banner-group__points" aria-label={`Other columns of ${heading}`}>
                {otherColumns.map(({ p, i }) => {
                  const name = pointName(p)
                  return (
                    <li key={`${i}:${p.kind}:${name}`} className="ts-banner-point">
                      <span className="ts-banner-point__name" title={p.kind === "category" ? p.label : undefined}>
                        {name}
                      </span>
                      <span className="ts-banner-point__words">
                        {p.kind === "condition"
                          ? ((catalogue && describeConditionGroups(p.condition, names, catalogue)) ??
                            "No condition yet.")
                          : ANSWER_IN_NUMBER}
                      </span>
                      <Button
                        size="small"
                        type="text"
                        danger
                        aria-label={`Remove ${name} from ${heading}`}
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => onPoints(withoutPoint(points ?? [], i))}
                      />
                    </li>
                  )
                })}
              </ul>
            </>
          )}
        </div>
      ) : points === null ? (
        <div className="ts-banner-group__body">
          <p className="ts-hint">
            {group.source === "data"
              ? "Every category, in the order of the prepared data."
              : "Every answer, in questionnaire order."}
          </p>
          <ul className="ts-banner-group__answers" aria-label={`Answers of ${heading}`}>
            {(question?.options ?? []).map((o) => (
              <li key={o.label} title={o.label}>
                {optionName(o.label)}
              </li>
            ))}
          </ul>
          <div className="ts-banner-group__actions">
            <Button
              size="small"
              disabled={!question}
              onClick={() => question && onPoints(materialisePoints(group, question))}
            >
              Choose columns
            </Button>
          </div>
        </div>
      ) : (
        <div className="ts-banner-group__body">
          {/* Only a banner saved by an earlier version can hold a group with no column. */}
          {points.length === 0 && (
            <p className="ts-hint">No columns: the group is left out of the banner.</p>
          )}
          <ul className="ts-banner-group__points" aria-label={`Columns of ${heading}`}>
            {points.map((p, i) => {
              const name = pointName(p)
              const last = points.length === 1
              return (
                // The position is part of the key: a stored list may name a label twice.
                <li
                  key={`${i}:${p.kind}:${p.kind === "category" ? p.label : p.name}`}
                  className="ts-banner-point"
                >
                  {p.kind === "category" ? (
                    <>
                      <span className="ts-banner-point__name" title={p.label}>
                        {name}
                      </span>
                      <DraftInput
                        className="ts-banner-point__rename"
                        aria-label={`Rename ${name} in ${heading}`}
                        placeholder={name}
                        value={p.rename ?? ""}
                        onCommit={(text) => commitRename(i, text)}
                      />
                    </>
                  ) : p.kind === "net" ? (
                    <>
                      <span className="ts-banner-point__name">
                        {p.name}
                        <span className="ts-chip ts-banner-point__net">Net</span>
                      </span>
                      <span className="ts-banner-point__members">
                        {p.members.map((m) => (
                          <Tag key={m} className="ts-tag" title={m}>
                            {optionName(m)}
                          </Tag>
                        ))}
                      </span>
                    </>
                  ) : (
                    // A range or a condition column: made elsewhere, shown in words here.
                    <>
                      <span className="ts-banner-point__name">{p.name}</span>
                      <span className="ts-banner-point__words">
                        {p.kind === "range"
                          ? rangeName(p.min, p.max)
                          : ((catalogue && describeConditionGroups(p.condition, names, catalogue)) ??
                            "No condition yet.")}
                      </span>
                    </>
                  )}
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Move ${name} up in ${heading}`}
                    icon={<FontAwesomeIcon icon={faArrowUp} />}
                    disabled={i === 0}
                    onClick={() => onPoints(movePoint(points, i, i - 1))}
                  />
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Move ${name} down in ${heading}`}
                    icon={<FontAwesomeIcon icon={faArrowDown} />}
                    disabled={i === points.length - 1}
                    onClick={() => onPoints(movePoint(points, i, i + 1))}
                  />
                  <Tooltip title={last ? LAST_COLUMN_STAYS : null}>
                    <span>
                      <Button
                        size="small"
                        type="text"
                        danger
                        aria-label={`Remove ${name} from ${heading}`}
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        disabled={last}
                        onClick={() => onPoints(removePoint(points, i))}
                      />
                    </span>
                  </Tooltip>
                </li>
              )
            })}
          </ul>
          <div className="ts-banner-group__actions">
            <Button size="small" disabled={!question} onClick={() => setAddingNet(true)}>
              Add net
            </Button>
            <Button size="small" type="link" className="ts-link" onClick={() => onPoints(null)}>
              Use every answer again
            </Button>
          </div>
        </div>
      )}

      <BannerNetModal
        open={addingNet}
        title={`Add a net to ${heading}`}
        options={netOptions}
        onCancel={() => setAddingNet(false)}
        onConfirm={confirmNet}
        getContainer={getContainer}
      />
    </section>
  )
}

export default BannerGroupEditor
